import { useEffect, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { Route, Switch, generatePath, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import ExistingTriggerSidePanel, { TRIGGER_EXAMPLE_CONTENT_WIDTH } from 'automation-ui-canvas/components/panels/ai-assistant-panel/ExistingTriggerSidePanel';
import { getExistingTriggerType } from 'automation-ui-enrollment/utils/EnrollmentDeciders';
import ActionConfigCombinedHeaderContainer from 'automation-ui-action-features/components/ActionConfigCombinedHeaderContainer';
import ConfigPanel from 'automation-ui-canvas/components/panels/ConfigPanel';
import ActionsOptionPanelContent from 'automation-ui-canvas/components/panels/ai-assistant-panel/ActionsOptionPanelContent';
import TriggersOptionPanelContent from 'automation-ui-canvas/components/panels/ai-assistant-panel/TriggersOptionPanelContent';
import * as AbsolutePaths from 'automation-ui-canvas/constants/AbsolutePaths';
import { AI_GENERATED_WORKFLOWS } from 'automation-ui-canvas/constants/Gates';
import { trackPageView } from 'automation-ui-canvas/utils/trackingUtils';
import { SIDE_PANEL_WIDTH } from 'automation-ui-core/constants/ui';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { parseLocation, joinPathAndQuery } from 'automation-ui-routing/utils';
import * as UsageTrackingScreens from 'automation-ui-usage-tracking/UsageTrackingScreens';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { LeftIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Text } from 'foundations-components/components/Text';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MaxHeightDiv = styled.div.withConfig({
  displayName: "AiAssistantPanel__MaxHeightDiv",
  componentId: "bas9md-0"
})(["height:100%;display:flex;flex-direction:row;"]);
const DivWithFixedWidthAndBorder = styled.div.withConfig({
  displayName: "AiAssistantPanel__DivWithFixedWidthAndBorder",
  componentId: "bas9md-1"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    borderRight: `1px solid ${theme.color['border-primary-subtle']}`,
    width: `${SIDE_PANEL_WIDTH}px`,
    padding: theme.space['600']
  };
});
export const AiAssistantPanel = ({
  canvasScrollElementRef,
  flowId,
  flow
}) => {
  const history = useHistory();
  const location = useLocation();
  const edgeId = parseLocation(location).edgeId;
  const {
    path,
    params: {
      assetType
    }
  } = useRouteMatch();
  const inReview = path === AbsolutePaths.AI_ASSISTANT_GENERATE_ASSET;
  const hasExistingTrigger = flow && !!getExistingTriggerType(flow.toHybridFlowInterface());
  const [showExistingTriggerPanel, setShowExistingTriggerPanel] = useState(false);
  useEffect(() => trackPageView(UsageTrackingScreens.AI_ASSISTANT), [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const isUngatedToAITriggers = useIsUngated(AI_GENERATED_WORKFLOWS);
  let backButtonLocation = null;
  if (assetType === 'actions' && edgeId) {
    backButtonLocation = joinPathAndQuery(generatePath(AbsolutePaths.ALL_ACTIONS_LIST, {
      flowId,
      screen: 'edit'
    }), {
      edgeId
    });
  } else if (assetType === 'triggers') {
    backButtonLocation = generatePath(AbsolutePaths.TRIGGER_SELECTION_ENROLLMENT_CONFIG, {
      flowId
    });
  }
  const getAdjustablePanelWidth = () => {
    if (!inReview && assetType === 'triggers') {
      if (showExistingTriggerPanel) {
        return SIDE_PANEL_WIDTH + TRIGGER_EXAMPLE_CONTENT_WIDTH;
      }
    }
    return SIDE_PANEL_WIDTH;
  };
  return /*#__PURE__*/_jsx(ConfigPanel, {
    dataTestId: "ai-assistant-panel",
    canvasScrollElementRef: canvasScrollElementRef,
    width: getAdjustablePanelWidth(),
    children: /*#__PURE__*/_jsxs(NavMarker, {
      name: "AI_ASSISTANT_PANEL",
      children: [/*#__PURE__*/_jsx(Route, {
        exact: true,
        path: [AbsolutePaths.AI_ASSISTANT],
        render: () => /*#__PURE__*/_jsxs(ActionConfigCombinedHeaderContainer, {
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            justifyContent: "center",
            children: [backButtonLocation && /*#__PURE__*/_jsx(IconButton, {
              use: "unstyled",
              marginInlineEnd: "200",
              onClick: () => {
                history.push(backButtonLocation);
              },
              children: /*#__PURE__*/_jsx(LeftIcon, {})
            }), /*#__PURE__*/_jsx(Text, {
              as: "h5",
              marginBlock: "0",
              display: "inline-block",
              children: /*#__PURE__*/_jsxs(Switch, {
                children: [/*#__PURE__*/_jsx(Route, {
                  path: AbsolutePaths.AI_ASSISTANT_ACTIONS,
                  children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                    message: "aiAssistantPanel.body.addActionsPanel.title"
                  })
                }), isUngatedToAITriggers && /*#__PURE__*/_jsxs(Route, {
                  path: AbsolutePaths.AI_ASSISTANT_TRIGGERS,
                  children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
                    message: "aiAssistantPanel.body.addTriggersPanel.title"
                  }), /*#__PURE__*/_jsx(Badge, {
                    use: "beta",
                    marginInlineStart: "100",
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "aiAssistantPanel.body.addTriggersPanel.triggerDescriptionSection.beta"
                    })
                  })]
                })]
              })
            })]
          }), /*#__PURE__*/_jsx(Button, {
            "data-test-id": "ai-assistant-panel-cancel-button",
            onClick: () => history.push(generatePath(AbsolutePaths.CANVAS_ROOT_WORKFLOW, {
              flowId
            })),
            use: "tertiary-light",
            size: "sm",
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "edit.cancel"
            })
          })]
        })
      }), /*#__PURE__*/_jsxs(MaxHeightDiv, {
        children: [/*#__PURE__*/_jsx(DivWithFixedWidthAndBorder, {
          children: /*#__PURE__*/_jsxs(Switch, {
            children: [/*#__PURE__*/_jsx(Route, {
              path: AbsolutePaths.AI_ASSISTANT_ACTIONS,
              render: () => /*#__PURE__*/_jsx(ActionsOptionPanelContent, {
                flowId: flowId
              })
            }), isUngatedToAITriggers && /*#__PURE__*/_jsx(Route, {
              path: AbsolutePaths.AI_ASSISTANT_TRIGGERS,
              render: () => /*#__PURE__*/_jsx(TriggersOptionPanelContent, {
                flowId: flowId,
                hasExistingTrigger: hasExistingTrigger,
                onClickShowExistingTrigger: () => {
                  setShowExistingTriggerPanel(true);
                }
              })
            })]
          })
        }), showExistingTriggerPanel && !inReview && flow && /*#__PURE__*/_jsx(ExistingTriggerSidePanel, {
          flow: flow,
          onClose: () => setShowExistingTriggerPanel(false)
        })]
      })]
    })
  });
};
export default AiAssistantPanel;